import { useCallback, useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import Dialog from "@/components/shared/dialog/Dialog";
import { useDeleteArticle } from "@/api/useArticles";
import { toast } from "sonner";

interface DeleteArticleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleId: string;
  articleTitle: string;
  onSuccess: () => void;
}

export function DeleteArticleDialog({
  open,
  onOpenChange,
  articleId,
  articleTitle,
  onSuccess,
}: DeleteArticleDialogProps) {
  const { mutateAsync: deleteArticle } = useDeleteArticle();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteArticle({ articleId });
      toast.success("Article deleted successfully");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error("Failed to delete article");
      console.error("Failed to delete article:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteArticle, articleId, onOpenChange, onSuccess]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="small">
        <Dialog.Title>Delete Article</Dialog.Title>
        <Flex direction="column" gap="4" p={"4"}>
          <Text size="2">
            Are you sure you want to delete the article "{articleTitle}"? This
            action cannot be undone.
          </Text>

          <Flex gap="3" justify="end" mt="2">
            <Button
              variant="soft"
              color="gray"
              onClick={handleCancel}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={isDeleting}
              disabled={isDeleting}
            >
              Delete Article
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
